import { createClient } from "@/lib/supabase/client";
import { Label } from "@radix-ui/react-dropdown-menu";
import { Flex, Separator } from "@radix-ui/themes";
import { User } from "@supabase/supabase-js";
import { CheckCircle2, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { AreYouSureAboutThat } from "../shared/AreYouSureAboutThat";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "../ui/input-otp";
import { Skeleton } from "../ui/skeleton";

export default function MFA({ user }: { user?: User | null }) {
  const supabase = createClient();

  const [factorId, setFactorId] = useState("");
  const [qr, setQR] = useState(""); // holds the QR code image SVG
  const [verifyCode, setVerifyCode] = useState(""); // contains the code entered by the user
  const [error, setError] = useState(""); // holds an error message

  // const [readyToShow, setReadyToShow] = useState(false);
  const [showMFAScreen, setShowMFAScreen] = useState(false);

  const [mfaDialogOpened, setMFADialogOpened] = useState(false);
  const [mfaCode, setMFACode] = useState("");
  const [showCode, setShowCode] = useState(false);

  const attemptVerifyCode = async () => {
    setError("");
    (async () => {
      const challenge = await supabase.auth.mfa.challenge({ factorId });
      if (challenge.error) {
        setError(challenge.error.message);
        throw challenge.error;
      }

      const challengeId = challenge.data.id;

      const verify = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code: verifyCode,
      });
      if (verify.error) {
        setError(verify.error.message);
        throw verify.error;
      }
      await checkMfa();
      setMFADialogOpened(false);
      console.log(verify);
    })();
  };

  // useEffect(() => {
  //   (async () => {
  //     const { data, error } = await supabase.auth.mfa.enroll({
  //       factorType: "totp",
  //     });
  //     if (error) {
  //       throw error;
  //     }

  //     setFactorId(data.id);

  //     // Supabase Auth returns an SVG QR code which you can convert into a data
  //     // URL that you can place in an <img> tag.
  //     setQR(data.totp.qr_code);
  //   })();
  // }, []);

  const enrolMfa = async () => {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
    });
    if (error) {
      throw error;
    }

    console.log(data);

    setFactorId(data.id);

    setMFACode(data.totp.secret);

    // Supabase Auth returns an SVG QR code which you can convert into a data
    // URL that you can place in an <img> tag.
    setQR(data.totp.qr_code);
  };

  const checkMfa = async () => {
    try {
      const { data, error } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) {
        throw error;
      }

      console.log(data);
      const { data: totp, error: totperror } =
        await supabase.auth.mfa.listFactors();
      if (totperror) {
        throw totperror;
      }
      console.log(totp);

      if (data.nextLevel === "aal2" && totp && totp.totp.length > 0) {
        setShowMFAScreen(true);

        setFactorId(totp.totp[0].id);
      } else {
        setShowMFAScreen(false);
        setMFACode("");
        setQR("");
        setFactorId("");
      }
    } finally {
      // setReadyToShow(true);
    }
  };

  useEffect(() => {
    console.log("test");
    (async () => checkMfa())();
  }, []);

  const unenrollMfa = async () => {
    const { error } = await supabase.auth.mfa.unenroll({
      factorId: factorId,
    });
    if (error) {
      throw error;
    }
    setFactorId("");
    setMFACode("");
    setQR("");
    await checkMfa();
  };

  return (
    <Card id="twoFactorAuthentication">
      <CardHeader>
        <CardTitle>Multi Factor Authentication</CardTitle>
        <CardDescription>
          Setup or reset multi factor authentication
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error}
        {/* {mfaResetError} */}
        {/* {mfaIsPending ||
          mfaResetIsPending ||
          (mfaVerifyIsPending && (
            <Flex className="w-full" justify="center" align="center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            </Flex>
          ))} */}
        {!user && <Skeleton className="w-full h-24" />}
        {showMFAScreen && user && (
          <Alert variant="default">
            <CheckCircle2 className="h-6 w-6" />
            <AlertTitle className="!pl-8">Multi factor enabled</AlertTitle>
            <AlertDescription className="!pl-8">
              You have multi factor authentication enabled, if you need to reset
              it you can do so below.
            </AlertDescription>
          </Alert>
        )}
        {(!showMFAScreen || !user) && (
          <Alert variant="default">
            <ShieldAlert className="h-6 w-6" />
            <AlertTitle className="!pl-8">Multi factor not enabled</AlertTitle>
            <AlertDescription className="!pl-8">
              Enabling multi factor authentication adds an extra layer of
              security to your accounts, making it harder for unauthorised users
              to gain access.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="border-t px-6 py-4">
        {showMFAScreen && (
          <AreYouSureAboutThat
            title="Reset Multi Factor Authentication"
            description="Are you sure you want to reset your multi factor authentication?"
            yesLabel="Reset"
            noLabel="Cancel"
            // formAction={mfaResetSubmitAction}
            action={unenrollMfa}
            destructive={true}
            triggerButton={
              <Button
                type="button"
                variant="destructive"
                // disabled={mfaResetIsPending}
              >
                Reset Multi Factor Authentication
              </Button>
            }
          />
        )}
        <Dialog
          onOpenChange={(val) => setMFADialogOpened(val)}
          open={mfaDialogOpened}
        >
          {!showMFAScreen && (
            <DialogTrigger asChild>
              <Button onClick={() => enrolMfa()}>
                Enable Multi Factor Authentication
              </Button>
            </DialogTrigger>
          )}

          <DialogContent onPointerDownOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Scan the QR code below</DialogTitle>
              <DialogDescription>
                You can use your authentication app of choice
              </DialogDescription>
            </DialogHeader>
            <Flex direction="column" gap="4" justify="center" align="center">
              <Flex className="w-full" justify="center" align="center">
                {qr && (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    src={qr}
                    alt="QR code"
                    width="250"
                    height="250"
                    className="rounded-lg"
                  />
                )}
                {!qr && <Skeleton className="w-[250px] h-[250px]" />}
              </Flex>

              <Flex
                direction="column"
                justify="center"
                align="center"
                className="max-w-[250px] mt-2"
              >
                <Flex>
                  <Button
                    onClick={() => setShowCode(!showCode)}
                    variant="secondary"
                    size="sm"
                  >
                    Having trouble?
                  </Button>
                </Flex>
                {showCode && (
                  <>
                    <Flex className="text-center mt-2 mb-2">
                      <p className="text-sm text-muted-foreground">
                        Cant scan the QR code? You can also use the following
                        code:
                      </p>
                    </Flex>
                    <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold">
                      {mfaCode}
                    </code>
                  </>
                )}
              </Flex>

              {/* <form action={mfaVerifySubmitAction} ref={mfaRef}> */}
              <Flex direction="column" className="mt-4">
                <Separator size="1" className="mb-6" />
                <Label className="mb-4 w-auto text-center">
                  Enter the code from your app
                </Label>
                <InputOTP
                  maxLength={6}
                  name="otp"
                  onComplete={() => attemptVerifyCode()}
                  onChange={(val) => setVerifyCode(val)}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

                <Button
                  type="submit"
                  className="mt-4"
                  // disabled={mfaVerifyIsPending}
                  onClick={() => attemptVerifyCode()}
                >
                  Verify
                </Button>
              </Flex>
              {/* </form> */}
            </Flex>
          </DialogContent>
        </Dialog>
      </CardFooter>
    </Card>
  );
}
